import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Lock, Eye, EyeOff, ShieldCheck } from 'lucide-react';
import api from '../api';

export default function ChangePasswordPage() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [showPwd, setShowPwd] = useState(false);
    const [showCurrentPwd, setShowCurrentPwd] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const isVoluntaryChange = !user?.must_change_password;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (isVoluntaryChange && !currentPassword) { setError('Current password is required'); return; }
        if (newPassword.length < 6) { setError('New password must be at least 6 characters'); return; }
        if (newPassword !== confirm) { setError('Passwords do not match'); return; }

        setLoading(true);
        try {
            await api.changePassword(newPassword, isVoluntaryChange ? currentPassword : null);
            // Redirect based on role
            if (user?.is_admin) navigate('/admin');
            else if (user?.is_partner) navigate('/partner/dashboard');
            else navigate('/');
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <main className="main" style={{ paddingTop: 0 }}>
            <div style={{
                background: 'linear-gradient(135deg, rgba(99,102,241,0.8) 0%, rgba(139,92,246,0.6) 100%)',
                height: '120px', marginBottom: '32px'
            }} />
            <div style={{ minHeight: 'calc(100vh - 200px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
                <div style={{ width: '100%', maxWidth: '480px' }}>
                    <div className="card" style={{ padding: '40px' }}>
                        {/* Icon + Title */}
                        <div style={{ textAlign: 'center', marginBottom: 28 }}>
                            <div style={{
                                width: 64, height: 64, borderRadius: '50%',
                                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                margin: '0 auto 16px'
                            }}>
                                <ShieldCheck size={32} color="#fff" />
                            </div>
                            <h1 style={{ fontSize: '24px', fontWeight: 700, marginBottom: 8 }}>Set Your Password</h1>
                            <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem' }}>
                                {user?.must_change_password
                                    ? 'This is your first login. Please set a new secure password to continue.'
                                    : 'Choose a new secure password for your account.'}
                            </p>
                        </div>

                        <form onSubmit={handleSubmit}>
                            {isVoluntaryChange && (
                                <div className="form-group">
                                    <label className="form-label">Current Password</label>
                                    <div style={{ position: 'relative' }}>
                                        <Lock size={18} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                                        <input
                                            type={showCurrentPwd ? 'text' : 'password'}
                                            className="form-input"
                                            style={{ paddingLeft: 44, paddingRight: 44 }}
                                            value={currentPassword}
                                            onChange={e => setCurrentPassword(e.target.value)}
                                            placeholder="Enter current password"
                                            required
                                        />
                                        <button type="button" onClick={() => setShowCurrentPwd(v => !v)}
                                            style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
                                            {showCurrentPwd ? <EyeOff size={18} /> : <Eye size={18} />}
                                        </button>
                                    </div>
                                </div>
                            )}

                            <div className="form-group">
                                <label className="form-label">New Password</label>
                                <div style={{ position: 'relative' }}>
                                    <Lock size={18} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                                    <input
                                        type={showPwd ? 'text' : 'password'}
                                        className="form-input"
                                        style={{ paddingLeft: 44, paddingRight: 44 }}
                                        value={newPassword}
                                        onChange={e => setNewPassword(e.target.value)}
                                        placeholder="Min. 6 characters"
                                        minLength={6}
                                        required
                                    />
                                    <button type="button" onClick={() => setShowPwd(v => !v)}
                                        style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
                                        {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </button>
                                </div>
                            </div>

                            <div className="form-group">
                                <label className="form-label">Confirm Password</label>
                                <div style={{ position: 'relative' }}>
                                    <Lock size={18} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                                    <input
                                        type={showPwd ? 'text' : 'password'}
                                        className="form-input"
                                        style={{ paddingLeft: 44 }}
                                        value={confirm}
                                        onChange={e => setConfirm(e.target.value)}
                                        placeholder="Repeat your new password"
                                        required
                                    />
                                </div>
                            </div>

                            {error && <div className="form-error" style={{ marginBottom: 16 }}>{error}</div>}

                            <button type="submit" className="btn btn-primary" style={{ width: '100%' }} disabled={loading}>
                                {loading ? 'Saving...' : isVoluntaryChange ? 'Update Password' : 'Set Password & Continue'}
                            </button>
                        </form>

                        <div style={{ textAlign: 'center', marginTop: 16 }}>
                            <button onClick={() => logout()}
                                style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: '0.85rem', cursor: 'pointer' }}>
                                Log out instead
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </main>
    );
}
